import { useContext, useEffect } from "react";
import Page, { PageDOMProps } from "../page";
import { useIntl } from "react-intl";
import Table, {TableColumn} from "../../table";
import { Dropdown } from "react-bootstrap";
import "./invitations-view.scss";
import { FeedbackContainerProps } from "../../feedback";
import { OrganizationGroup } from "../../../model/OrganizationGroup";
import { Link } from "react-router-dom";
import RevokeInvitationModal from "../../modals/revoke-invitation-modal";
import RemoveInvitationModal from "../../modals/remove-invitation-modal";
import ResendInvitationModal from "../../modals/resend-invitation-modal";
import ViewInvitationModal from "../../modals/view-invitation-modal";
import ViewGroupModal from "../../modals/view-group-modal";
import CreateInvitationModal from "../../modals/create-invitation-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import InvitationStateBadge from "../../badges/invitation-state-badge";
import ShowFeedback from "../../show-feedback";
import {
  OrganizationGroupInvitationLabels,
  OrganizationGroupInvitationValues,
} from "../../../localization/organization-group-invitation";
import { OrganizationGroupValues } from "../../../localization/organization-group";
import {resolveColumns, resolveFilters} from "../../../ui-configuration/configuration-tools";
import {OrganizationGroupInvitationWithExpiredStatus} from "../../../utils/invitation";
import {TableFilter} from "../../table/table-container";
import {HideLabelTableFilterView, TableFilterViewProps} from "../../table/table-filter/table-filter-view";

export enum ModalKeys {
  show = "show",
  showGroup = "show-group",
  revoke = "revoke",
  resend = "resend",
  remove = "remove",
  create = "create",
}
//<editor-fold desc="Props">

export interface InvitationsModalVisibilityProps {
  showModal?: { key: ModalKeys; invitationId?: string; secondaryId?: string };
  onShowModal: (
    key: ModalKeys,
    invitationId?: string,
    secondaryId?: string
  ) => void;
  onHideModal: (callback?: () => void) => void;
}
export interface InvitationsDOMProps extends PageDOMProps {}

export interface InvitationsStateProps extends FeedbackContainerProps {
  selected: OrganizationGroupInvitationWithExpiredStatus[];
  onSetSelected: (selection: OrganizationGroupInvitationWithExpiredStatus[]) => void;
}
export interface InvitationsProps
  extends InvitationsStateProps,
    InvitationsDOMProps,
    InvitationsModalVisibilityProps {
  userName?: string;
  userId?: string;
  invitations?: OrganizationGroupInvitationWithExpiredStatus[];
  groups?: OrganizationGroup[];
  orgAdminRoleId?: string;
  onLoadInvitations: () => Promise<any>;
  onLoadGroups: () => void;
  onLoadOrganizationRoles: () => void;
}
//</editor-fold>

//<editor-fold desc="Messages">
//</editor-fold>

//<editor-fold desc="Utils">

const resolveRowClasses = (
  row: OrganizationGroupInvitationWithExpiredStatus,
  rowIndex: number
) => {
  return row.invitationState;
};
/**
 * Internal utility for creating a invitation status filter
 * @param status
 * @param initial
 * @param intl
 */
function createStatusFilter(status: "deliveryRequested"|"updated"|"created"|"revoked"|"accepted"|"declined"|"expired",
                            initial: boolean): TableFilter {
  return {
    key: "hide" + status.replace(/^\w/, (c) => c.toUpperCase()),
    initial: initial,
    view: (props: TableFilterViewProps) => <HideLabelTableFilterView
        label={OrganizationGroupInvitationValues.invitationState[status]}
        {...props}
    />,
    apply: (i: OrganizationGroupInvitationWithExpiredStatus) => {
      return i.invitationState !==  status;
    },
  };
}
//</editor-fold>

function Invitations(props: InvitationsProps) {
  //<editor-fold desc="Local variables">
  const {
    invitations,
    groups,
    onLoadInvitations,
    onLoadGroups,
    onLoadOrganizationRoles,
    selected,
    onSetSelected,
    onShowModal,
    onHideModal,
    showModal,
    onShowFeedback,
    onHideFeedback,
    feedback,
    orgAdminRoleId,
    userName,
    userId,
    ...others
  } = props;
  // this is more like a variable than a hook
  const intl = useIntl();
  const title = intl.formatMessage({
    defaultMessage: "Manage invitations",
    description: "Heading for the Manage invitations page",
  });
  const description = intl.formatMessage({
    defaultMessage: "Invitation management",
    description: "window meta description for the Manage invitations page",
  });

  const { conf } = useContext(UIConfiguration);
  const invitationsConf =
    conf.functionality && conf.functionality.invitations
      ? conf.functionality.invitations
      : {};

  let columns: TableColumn[] = [
    {
      key: "id",
      label: intl.formatMessage(OrganizationGroupInvitationLabels.id),
      isTechnical: true,
      hidden: true,
    },
    {
      key: "invitationStatus",
      isDummy: true,
      sortable: true,
      label: intl.formatMessage(
        OrganizationGroupInvitationLabels.invitationState
      ),
      rendererData: {
        resolveValue: (itm: any, rd: any) =>
          intl.formatMessage(
            (OrganizationGroupInvitationValues.invitationState as any)[
              itm.invitationState as string
            ]
          ),
      },
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return (
          <InvitationStateBadge
            state={props.row.invitationState}
            withLabel={true}
            validUntil={props.row.validUntil}
            validFrom={props.row.validFrom}
          />
        );
      },
      tipRenderer: () => false,
    },
    {
      key: "email",
      sortable: true,
      label: intl.formatMessage(OrganizationGroupInvitationLabels.email),
    },
    {
      key: "recipientName",
      sortable: true,
      label: intl.formatMessage(
        OrganizationGroupInvitationLabels.recipientName
      ),
    },
    {
      key: "groups",
      sortable: true,
      isDummy: true,
      label: intl.formatMessage(OrganizationGroupInvitationLabels.groupIds),
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        const g: OrganizationGroup[] = props.rendererData.resolveValue(
          props.row,
          props.rendererData
        );
        return (
          <>
            {props.rendererData.invitationsConf.showGroup &&
              g &&
              g.map((val: OrganizationGroup, ind) => {
                if (val) {
                  return (
                    <span key={"link_" + val.id}>
                      <Link
                        to={
                          "/invitations/" + ModalKeys.showGroup + "/" + val.id
                        }
                      >
                        {val.name
                          ? val.name
                          : intl.formatMessage(
                              OrganizationGroupValues.name.undefined
                            )}
                      </Link>
                      {ind < g.length - 1 ? <>, </> : undefined}
                    </span>
                  );
                }
                return null;
              })}
            {!props.rendererData.invitationsConf.showGroup &&
              g &&
              g.map((val: OrganizationGroup, ind) => {
                if (val) {
                  return (
                    <span key={"link_" + val.id}>
                      {val.name
                        ? val.name
                        : intl.formatMessage(
                            OrganizationGroupValues.name.undefined
                          )}
                      {ind < g.length - 1 ? <>, </> : undefined}
                    </span>
                  );
                }
                return null;
              })}
          </>
        );
      },
      tipRenderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        const g: OrganizationGroup[] = props.rendererData.resolveValue(
          props.row,
          props.rendererData
        );
        return (
          <>
            {g &&
              g.map((val: OrganizationGroup, ind) => {
                let t;
                if (val) {
                  t = val && val.name ? val.name : "unknown";
                  if (ind < g.length - 1) {
                    t = t + ", ";
                  }
                }
                return t;
              })}
          </>
        );
      },
      rendererData: {
        groups,
        invitationsConf: invitationsConf,
        resolveValue: (itm: any, rd: any) => {
          return itm.groupIds
            .map((val: string) => {
              const g = rd.groups
                ? rd.groups.find((v: OrganizationGroup) => v.id === val)
                : undefined;
              if (g) {
                g.toString = () => g.name;
              }
              return g;
            })
            .sort((a: OrganizationGroup, b: OrganizationGroup) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            });
        },
      },
    },
    {
      key: "orgAdmin",
      sortable: true,
      isDummy: true,
      hidden: true,
      label: intl.formatMessage(OrganizationGroupInvitationLabels.isOrgAdmin),
      rendererData: {
        resolveValue: (itm: any, rd: any) => {
          return itm.organizationRoleIds.find(
            (val: string) => val === orgAdminRoleId
          )
            ? [
                true,
                intl.formatMessage(
                  OrganizationGroupInvitationValues.isOrgAdmin.true
                ),
              ]
            : [
                false,
                intl.formatMessage(
                  OrganizationGroupInvitationValues.isOrgAdmin.false
                ),
              ];
        },
      },
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        const g: any[] = props.rendererData.resolveValue(
          props.row,
          props.rendererData
        );
        return (
          <>
            {g[0] === true && (
              <span className={"indicator text-success mx-1"}>
                <FontAwesomeIcon icon={"check"} />
              </span>
            )}
            {g[0] === false && (
              <span className={"indicator text-danger mx-1"}>
                <FontAwesomeIcon icon={"times"} />
              </span>
            )}
          </>
        );
      },
      tipRenderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        const g: any[] = props.rendererData.resolveValue(
          props.row,
          props.rendererData
        );
        return (
          <>
            {g[0] === true && (
              <span className={"indicator  mx-1"}>
                <FontAwesomeIcon icon={"check"} className={"mr-1"} /> {g[1]}
              </span>
            )}
            {g[0] === false && (
              <span className={"indicator mx-1"}>
                <FontAwesomeIcon icon={"times"} className={"mr-1"} /> {g[1]}
              </span>
            )}
          </>
        );
      },
    },
    {
      key: "validFrom",
      label: intl.formatMessage(OrganizationGroupInvitationLabels.validFrom),
      sortable: true,
      hidden: true,
      rendererData: {
        resolveValue: (itm: any) => {
          let retVal;
          const d = itm.validFrom;
          if (d === "now()") {
            retVal = intl.formatDate(new Date());
          } else if (d) {
            retVal = intl.formatDate(new Date(d));
          }
          return retVal;
        },
      },
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return <>{props.rendererData.resolveValue(props.row)}</>;
      },
    },

    {
      key: "validUntil",
      label: intl.formatMessage(OrganizationGroupInvitationLabels.validUntil),
      sortable: true,
      hidden: true,
      rendererData: {
        resolveValue: (itm: any) => {
          let retVal;
          const d = itm.validUntil;
          if (d === "now()") {
            retVal = intl.formatDate(new Date());
          } else if (d) {
            retVal = intl.formatDate(new Date(d));
          }
          return retVal;
        },
      },
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return <>{props.rendererData.resolveValue(props.row)}</>;
      },
    },
  ];
  //</editor-fold>

  if (invitationsConf.columns && invitationsConf.columns.length > 0) {
    columns = resolveColumns(columns, invitationsConf.columns);
  }
  //<editor-fold desc="Hooks">
  // resets selection when users are undefined, and clears out selected items that are not part of updated data
  useEffect(() => {
    if (selected.length) {
      if (invitations === undefined) {
        onSetSelected([]);
      } else {
        const ids = invitations.map(
          (inv: OrganizationGroupInvitationWithExpiredStatus) => inv.id
        );
        const newS = selected.filter((itm: any) => {
          return ids.indexOf(itm.id) >= 0;
        });
        if (newS.length !== selected.length) {
          onSetSelected(newS);
        }
      }
    }
  }, [invitations, selected, onSetSelected]);
  useEffect(() => {
    if (groups === undefined) {
      onLoadGroups();
    }
  }, [groups, onLoadGroups]);

  useEffect(() => {
    if (orgAdminRoleId === undefined) {
      onLoadOrganizationRoles();
    }
  }, [orgAdminRoleId, onLoadOrganizationRoles]);
  const filters: TableFilter[] = resolveFilters([
    createStatusFilter('deliveryRequested', false),
    createStatusFilter('updated', false),
    createStatusFilter('created', false),
    createStatusFilter('revoked', false),
    createStatusFilter('accepted', false),
    createStatusFilter('declined', false),
    createStatusFilter('expired', false),
   ], invitationsConf?.filters);
  //</editor-fold>
  return (
    <Page
      header={<h1>{title}</h1>}
      data-test-invitations-page
      id={"invitations-page"}
      meta={{
        title,
        description,
      }}
      {...others}
    >
      <ShowFeedback
        idPrefix={"invitations_"}
        onHideFeedback={onHideFeedback}
        feedback={feedback}
      />
      <Table<OrganizationGroupInvitationWithExpiredStatus>
        data-test-invitations-table
        persistStateKey={"invitations-table"}
        allowColumnSort={true}
        selection={{
          multi: false,
          selectAll: false,
        }}
        filters={filters}
        header={
          <>
            {invitationsConf.create && (
              <button
                data-test-create-invitation-modal-trigger
                onClick={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShowModal(ModalKeys.create);
                }}
                className={"mr-auto btn btn-theme-to-primary"}
              >
                {intl.formatMessage({
                  defaultMessage: "Create",
                  description: "Label for the create invitation button",
                })}
              </button>
            )}
          </>
        }
        search={true}
        rowClasses={resolveRowClasses}
        selected={selected}
        onSelectionChanged={onSetSelected}
        columns={columns}
        columnToggle
        reload={true}
        onLoadData={onLoadInvitations}
        data={invitations ? invitations : undefined}
        identifyingColumn={"id"}
        pagination={false}
        rowTools={
          invitationsConf.show ||
          invitationsConf.resend ||
          invitationsConf.revoke ||
          invitationsConf.remove
            ? (props: { rowEntry: OrganizationGroupInvitationWithExpiredStatus }) => {
                return (
                  <>
                    {invitationsConf.show && (
                      <Dropdown.Item
                        data-test-row-tool-invitation-details={
                          props.rowEntry.id
                        }
                        onClick={(e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onShowModal(ModalKeys.show, props.rowEntry.id);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "View details",
                          description: "Label for the tool",
                        })}
                      </Dropdown.Item>
                    )}
                    {props.rowEntry.invitationState !== "declined" &&
                      props.rowEntry.invitationState !== "revoked" &&
                      props.rowEntry.invitationState !== "accepted" && (
                        <>
                          {invitationsConf.resend && (
                            <Dropdown.Item
                              data-test-row-tool-invitation-item
                              data-test-row-tool-resend-invitation={
                                props.rowEntry.id
                              }
                              onClick={(e: any) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onShowModal(
                                  ModalKeys.resend,
                                  props.rowEntry.id
                                );
                              }}
                            >
                              {intl.formatMessage({
                                defaultMessage: "Resend",
                                description: "Label for the tool",
                              })}
                            </Dropdown.Item>
                          )}
                          {invitationsConf.revoke && (
                            <Dropdown.Item
                              data-test-row-tool-revoke-invitation={
                                props.rowEntry.id
                              }
                              onClick={(e: any) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onShowModal(
                                  ModalKeys.revoke,
                                  props.rowEntry.id
                                );
                              }}
                            >
                              {intl.formatMessage({
                                defaultMessage: "Revoke",
                                description: "Label for the tool",
                              })}
                            </Dropdown.Item>
                          )}
                        </>
                      )}
                    {invitationsConf.remove && (
                      <Dropdown.Item
                        data-test-row-tool-remove-invitation={props.rowEntry.id}
                        onClick={(e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onShowModal(ModalKeys.remove, props.rowEntry.id);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "Remove",
                          description: "Label for the tool",
                        })}
                      </Dropdown.Item>
                    )}
                  </>
                );
              }
            : undefined
        }
      />
      {invitationsConf.show && (
        <ViewInvitationModal
          show={showModal ? showModal.key === ModalKeys.show : false}
          onClose={onHideModal}
          invitationId={showModal?.invitationId}
        />
      )}
      {invitationsConf.resend && (
        <ResendInvitationModal
          show={showModal ? showModal.key === ModalKeys.resend : false}
          onClose={onHideModal}
          invitationId={showModal?.invitationId}
          onShowFeedback={onShowFeedback}
        />
      )}
      {invitationsConf.revoke && (
        <RevokeInvitationModal
          show={showModal ? showModal.key === ModalKeys.revoke : false}
          onClose={onHideModal}
          invitationId={showModal?.invitationId}
          onShowFeedback={onShowFeedback}
        />
      )}
      {invitationsConf.remove && (
        <RemoveInvitationModal
          show={showModal ? showModal.key === ModalKeys.remove : false}
          onClose={onHideModal}
          invitationId={showModal?.invitationId}
          onShowFeedback={onShowFeedback}
        />
      )}
      {invitationsConf.showGroup && (
        <ViewGroupModal
          show={showModal ? showModal.key === ModalKeys.showGroup : false}
          onClose={onHideModal}
          onShowFeedback={onShowFeedback}
          groupId={showModal?.secondaryId}
        />
      )}
      {invitationsConf.create && (
        <CreateInvitationModal
          show={showModal ? showModal.key === ModalKeys.create : false}
          onClose={onHideModal}
          onShowFeedback={onShowFeedback}
        />
      )}
    </Page>
  );
}

export default Invitations;
